import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input, Label, Textarea } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { KpiCard } from "../components/dashboard/KpiCard";
import { toast, Toaster } from "sonner";
import { Plus, X, Search, Sparkles, Wrench, Clock, AlertOctagon, TrendingUp } from "lucide-react";
import { format, isBefore, formatDistanceToNow } from "date-fns";

const priMap = { critical: "critical", high: "warning", medium: "warning", low: "success" };
const statusMap = {
  todo: { v: "neutral", label: "To do" },
  in_progress: { v: "info", label: "In progress" },
  in_review: { v: "warning", label: "In review" },
  blocked: { v: "critical", label: "Blocked" },
  done: { v: "success", label: "Done" },
  escalated: { v: "critical", label: "Escalated" },
};

export default function RemediationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const empty = { title: "", description: "", owner_id: "", due_date: "", priority: "medium" };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      if (priority) params.set("priority", priority);
      if (overdueOnly) params.set("overdue", "true");
      const [list, dash] = await Promise.all([
        api.get(`/remediations?${params.toString()}`),
        api.get("/remediations/dashboard"),
      ]);
      setItems(list.data.items || []);
      setDashboard(dash.data);
    } finally { setLoading(false); }
  }, [q, status, priority, overdueOnly]);

  useEffect(() => {
    load();
    api.get("/users?limit=200").then(({ data }) => setUsers(data.items || [])).catch(() => {});
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (form.due_date) payload.due_date = new Date(form.due_date).toISOString();
      else delete payload.due_date;
      if (!payload.owner_id) delete payload.owner_id;
      await api.post("/remediations", payload);
      toast.success("Remediation created");
      setShowForm(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const runAutoGen = async () => {
    try {
      const { data } = await api.post("/remediations/auto-generate");
      toast.success(`Created ${data.created} remediations, escalated ${data.escalated}`);
      load();
    } catch (err) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Remediations</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track corrective actions from audit findings, risks and compliance gaps. Auto-escalation for overdue tasks.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runAutoGen} data-testid="remediation-autogen">
            <Sparkles className="w-4 h-4" /> Auto-generate
          </Button>
          <Button data-testid="remediation-new" onClick={() => { setForm(empty); setShowForm(true); }}>
            <Plus className="w-4 h-4" /> New task
          </Button>
        </div>
      </div>

      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard testId="rem-kpi-total" title="Total" value={dashboard.totals.total} icon={Wrench} kind="info" />
          <KpiCard testId="rem-kpi-overdue" title="Overdue" value={dashboard.totals.overdue} icon={AlertOctagon} kind="critical" />
          <KpiCard testId="rem-kpi-due-soon" title="Due in 7 days" value={dashboard.totals.due_soon} icon={Clock} kind="warning" />
          <KpiCard testId="rem-kpi-avg" title="Avg. progress" value={dashboard.totals.avg_progress} suffix="%" icon={TrendingUp} kind="success" />
        </div>
      )}

      {dashboard && dashboard.by_owner.length > 0 && (
        <Card data-testid="rem-by-owner">
          <CardHeader><CardTitle>Workload by owner</CardTitle><CardDescription>Open remediation tasks per owner.</CardDescription></CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {dashboard.by_owner.map((o) => (
                <li key={o.owner_id} className="px-6 py-3 flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-primary-subtle text-primary flex items-center justify-center text-xs font-semibold shrink-0">
                    {o.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{o.name}</div>
                    <div className="text-xs text-muted-foreground">{o.open} open · {o.overdue} overdue · {o.critical} critical</div>
                  </div>
                  <div className="w-40 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary" style={{ width: `${Math.min(100, o.open * 10)}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="rem-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="pl-9" placeholder="Search remediations…" />
            </div>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="rem-status" className="h-10 w-full md:w-44 rounded-md border border-border bg-card px-3 text-sm">
              <option value="">All statuses</option>
              {Object.entries(statusMap).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="rem-priority" className="h-10 w-full md:w-40 rounded-md border border-border bg-card px-3 text-sm">
              <option value="">All priorities</option>
              {["critical", "high", "medium", "low"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <label className="inline-flex items-center gap-2 text-sm h-10 px-3 border border-border rounded-md cursor-pointer hover:bg-muted">
              <input type="checkbox" data-testid="rem-overdue" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} />
              Overdue only
            </label>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="border-t border-b border-border">
                  <th className="text-left py-2.5 px-4 label-eyebrow">Task</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Owner</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Priority</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Status</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Progress</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Due</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Esc.</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Loading…</td></tr>}
                {!loading && items.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">No remediations.</td></tr>}
                {items.map((r) => {
                  const st = statusMap[r.status] || { v: "neutral", label: r.status };
                  const overdue = r.due_date && r.status !== "done" && isBefore(new Date(r.due_date), new Date());
                  return (
                    <tr key={r.id} data-testid={`rem-row-${r.id}`}
                        onClick={() => navigate(`/remediations/${r.id}`)}
                        className="border-b border-border hover:bg-muted/40 cursor-pointer transition-colors">
                      <td className="py-3 px-4 font-medium max-w-sm">
                        <div className="truncate">{r.title}</div>
                        {r.source_type && <div className="text-xs text-muted-foreground">source: {r.source_type}</div>}
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">{r.owner_name || "—"}</td>
                      <td className="py-3 px-4"><Badge variant={priMap[r.priority]}>{r.priority}</Badge></td>
                      <td className="py-3 px-4"><Badge variant={st.v}>{st.label}</Badge></td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2 w-32">
                          <div className="h-1.5 bg-muted rounded-full overflow-hidden flex-1">
                            <div className={r.progress === 100 ? "h-full bg-green-500" : "h-full bg-primary"} style={{ width: `${r.progress || 0}%` }} />
                          </div>
                          <span className="tabular-nums text-xs w-8 text-right">{r.progress || 0}%</span>
                        </div>
                      </td>
                      <td className={"py-3 px-4 tabular-nums " + (overdue ? "text-red-600 font-medium" : "text-muted-foreground")}>
                        {r.due_date ? format(new Date(r.due_date), "MMM d, yyyy") : "—"}
                      </td>
                      <td className="py-3 px-4">
                        {r.escalation_level > 0 && <Badge variant="critical">L{r.escalation_level}</Badge>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-lg card-surface animate-fade-in" onClick={(e) => e.stopPropagation()} data-testid="rem-form-modal">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-base font-semibold">New remediation</h3>
              <button onClick={() => setShowForm(false)} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div><Label>Title</Label><Input data-testid="rem-form-title" className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
              <div><Label>Description</Label><Textarea className="mt-1.5" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Owner</Label>
                  <select className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm mt-1.5" value={form.owner_id}
                          onChange={(e) => setForm({ ...form, owner_id: e.target.value })}>
                    <option value="">Assign to me</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <select className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm mt-1.5" value={form.priority}
                          onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                    {["critical", "high", "medium", "low"].map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label>Due date</Label>
                <Input type="date" className="mt-1.5" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" data-testid="rem-form-submit">Create</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
